import Breadcrumb from "@components/base/breadcrumb";
import Button from "@components/base/button";
import Modal from "@components/base/modal";
import SearchInput from "@components/base/search-input";
import Select from "@components/base/select";
import { NavLabels, SubNavLabels } from "@components/Layout";
import { css } from "@emotion/react";
import useAppDispatch from "@hooks/useAppDispatch";
import useAppSelector from "@hooks/useAppSelector";
import { hidePopUp, showPopUp } from "@store/ui/uiSlice";
import theme from "@styles/theme";
import { Option } from "baseui/select";
import i18next from "i18next";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IoAdd } from "react-icons/io5";
import EditRecurringIssue from "./components/EditRecurringIssue";
import NewRecurringIssueForm from "./components/NewRecurringIssueForm";
import TasksTable from "./components/RecurringIssueTable";

import { IssueType } from "@store/issue/types";
import { selectCurrentRecurringIssue } from "@store/recurringIssues/recurringIssueSelectors";
import { setSelectedRecurringIssueId } from "@store/recurringIssues/recurringIssueSlice";
import {
  deleteRecurringIssue,
  fetchRecurringIssues,
} from "@store/recurringIssues/recurringIssueThunk";
import { Month, months } from "@store/recurringIssues/types";
import { fetchSpaces } from "@store/space/spaceThunk";
import { debounce } from "lodash";

export const RecurringIssueTypes = [
  { id: "all", label: i18next.t("tasks.all") },
  { id: IssueType.TECHNICAL, label: i18next.t("tasks.technical") },
  { id: IssueType.HOUSEKEEPING, label: i18next.t("tasks.housekeeping") },
  { id: IssueType.GENERAL, label: i18next.t("tasks.general") },
];
const RecurringIssueScreen: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [selectedMonth, setSelectedMonth] = useState<Option[]>([{ id: "all" }]);
  const [selectedType, setSelectedType] = useState<Option[]>([{ id: "all" }]);
  const [search, setSearch] = useState("");
  const [showDeleteWindow, setShowDeleteWindow] = useState(false);
  const popUp = useAppSelector(({ ui }) => ui.web.popUp);
  const currentIssue = useAppSelector(selectCurrentRecurringIssue);
  const [loading, setLoading] = useState(true);

  const monthOptions: Option[] = [
    { id: "all", label: t("months.all") },
    ...months.map((month: Month) => ({
      id: month,
      label: t(`months.${month}`),
    })),
  ];

  const delayedQuery = useRef(
    debounce(
      (search: string, issueType?: IssueType, month?: Month) =>
        getRecurringIssues(search, issueType, month),
      750,
    ),
  ).current;

  const getRecurringIssues = async (
    search: string,
    issueType?: IssueType,
    period?: Month,
  ) => {
    setLoading(true);
    try {
      dispatch(
        fetchRecurringIssues({
          issueType,
          period,
          search: search.trim() || undefined,
        }),
      );
    } catch (error) {
      /* empty */
    }
    setLoading(false);
  };

  const getTypeFilter = (type: Option["id"] = selectedType[0].id) =>
    type === "all" ? undefined : (type as IssueType);

  const getMonthFilter = (month: Option["id"] = selectedMonth[0].id) => {
    return month === "all" ? undefined : (month as Month);
  };
  const onSearchChange = (value: string) => {
    setSearch(value);
    delayedQuery(value, getTypeFilter(), getMonthFilter());
  };
  const onMonthChange = ({ id }: Option) => {
    setSelectedMonth([{ id }]);
    delayedQuery(search, getTypeFilter(), getMonthFilter(id));
  };

  const onTypeChange = ({ id }: Option) => {
    setSelectedType([{ id }]);
    delayedQuery(search, getTypeFilter(id), getMonthFilter());
  };

  const onShowDeleteWindow = () => {
    dispatch(hidePopUp());
    setShowDeleteWindow(true);
  };

  const onDelete = () => {
    if (currentIssue === undefined) return;
    dispatch(
      deleteRecurringIssue({ id: currentIssue.id, label: currentIssue.label }),
    );
    setShowDeleteWindow(false);
  };

  useEffect(() => {
    dispatch(fetchSpaces());
    dispatch(fetchRecurringIssues());
    setLoading(false);
  }, []);

  const onSubmit = () => {
    setSelectedMonth([{ id: "all" }]);
    setSelectedType([{ id: "all" }]);
    setSearch("");
    dispatch(fetchRecurringIssues());
  };

  useEffect(() => {
    if (!popUp && !showDeleteWindow) {
      dispatch(setSelectedRecurringIssueId());
    }
  }, [popUp, showDeleteWindow, dispatch]);

  const onClickNewTask = () => {
    dispatch(showPopUp({ type: "recurringIssue/new", pos: { x: 0, y: 0 } }));
  };

  return (
    <>
      <Breadcrumb labels={[SubNavLabels.Settings, NavLabels.RecurringIssues]} />
      <section css={styles.section}>
        <div css={styles.actionbar}>
          <div css={styles.action}>
            <div>
              <h4>{t("tasks.months")}</h4>
              <Select
                options={monthOptions}
                value={selectedMonth}
                onChange={({ option }) => onMonthChange(option)}
                containerStyle={{ width: "200px" }}
                cleanable={false}
              />
            </div>
            <div>
              <h4>{t("tasks.type")}</h4>
              <Select
                options={RecurringIssueTypes}
                value={selectedType}
                onChange={({ option }) => onTypeChange(option)}
                containerStyle={{ width: "200px" }}
                cleanable={false}
              />
            </div>
            <SearchInput
              value={search}
              onChange={onSearchChange}
              placeholder={t("search.taskRoom")}
              containerStyle={{ width: "300px", alignSelf: "end" }}
            />
          </div>
          <Button onClick={onClickNewTask}>
            <IoAdd size={24} />
            {t("tasks.addNew")}
          </Button>
        </div>

        <TasksTable search={search} loading={loading} />

        {popUp?.type === "recurringIssue/edit" && (
          <EditRecurringIssue onShowDeleteWindow={onShowDeleteWindow} />
        )}
        {popUp?.type === "recurringIssue/new" && (
          <NewRecurringIssueForm
            refetch={onSubmit}
            selectedIssueId={currentIssue?.id}
          />
        )}
        <Modal
          title={t("tasks.delete.title")}
          buttonText={t("issue.delete.action")}
          buttonAction={onDelete}
          center
          isOpen={showDeleteWindow}
          setShowModal={setShowDeleteWindow}>
          {t("tasks.delete.description", { value: currentIssue?.label })}
        </Modal>
      </section>
    </>
  );
};

const styles = {
  section: css({
    display: "flex",
    flex: 1,
    flexDirection: "column",
    padding: theme.sizing.scale1600,
    overflow: "scroll",
  }),
  actionbar: css({
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: theme.sizing.scale800,
  }),
  actions: css({ display: "flex", flexDirection: "row", alignItems: "center" }),
  action: css({ display: "flex", gap: theme.sizing.scale400 }),
};

export default RecurringIssueScreen;
